import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Copy, ExternalLink, FolderTree, Package, Share2, Star, BadgePercent, Store, Tag } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/store/authStore'
import { categoriesApi } from '@/services/categoriesApi'
import { getProducts, type Product } from '@/lib/api/products'
import {
  buildCatalogShareMessage,
  buildWhatsAppShareLink,
  formatMoney,
  getPublicCatalogPath,
  getPublicCatalogUrl,
} from '@/lib/catalogLinks'
import { buildPromotionCreateUrl } from '@/lib/promotionLinks'
import CatalogNavTabs from '@/components/catalog/CatalogNavTabs'
import { useVerticalUI } from '@/lib/verticalUI'

const DEFAULT_CATEGORY = 'Sin Categoría'

export default function Catalog() {
  const { profile } = useVerticalUI()
  const tenant = useAuthStore((s) => s.tenant)
  const tenantName = tenant?.name || 'Tu tienda'
  const tenantSlug = tenant?.slug || ''
  const [products, setProducts] = useState<Product[]>([])
  const [categoryCount, setCategoryCount] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true

    const load = async () => {
      try {
        setLoading(true)
        const [productsData, categoriesData] = await Promise.all([getProducts(), categoriesApi.getAll()])

        if (!mounted) return
        setProducts(productsData)
        setCategoryCount(categoriesData.length)
      } catch (error) {
        console.error('Error loading catalog overview:', error)
        if (mounted) toast.error('No se pudo cargar el catálogo')
      } finally {
        if (mounted) setLoading(false)
      }
    }

    load()
    return () => {
      mounted = false
    }
  }, [])

  const featured = useMemo(() => products.filter((product) => product.isFeatured).slice(0, 4), [products])
  const latest = useMemo(() => products.slice(0, 6), [products])

  const publicCatalogPath = tenantSlug ? getPublicCatalogPath(tenantSlug) : '/app/products'
  const publicCatalogUrl = tenantSlug ? getPublicCatalogUrl(tenantSlug) : ''

  const openPublicCatalog = () => {
    if (!publicCatalogUrl) return
    window.open(publicCatalogUrl, '_blank', 'noopener,noreferrer')
  }

  const copyCatalogLink = async () => {
    if (!publicCatalogUrl) return
    await navigator.clipboard.writeText(publicCatalogUrl)
    toast.success('Enlace copiado')
  }

  const shareCatalogByWhatsApp = () => {
    if (!publicCatalogUrl) return
    const message = buildCatalogShareMessage({
      tenantName,
      url: publicCatalogUrl,
    })
    window.open(buildWhatsAppShareLink(message), '_blank', 'noopener,noreferrer')
  }

  return (
    <div data-tour="catalog-main" className="space-y-6 pb-8">
      <CatalogNavTabs />
      <section className="overflow-hidden rounded-[2rem] border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-teal-50 shadow-sm">
        <div className="grid gap-6 px-5 py-6 lg:grid-cols-[1.4fr_0.8fr] lg:px-8 lg:py-8">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">
              <Store className="h-3.5 w-3.5" />
              {profile.terminology.catalogName}
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tight text-slate-900 lg:text-4xl">
                {profile.slug === 'otro' || profile.slug === 'tienda'
                  ? 'Tu catálogo como vitrina de ventas'
                  : `Gestión de ${profile.terminology.catalogName}`}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600 lg:text-base">
                {profile.slug === 'otro' || profile.slug === 'tienda'
                  ? 'Reúne productos, categorías y enlaces de WhatsApp en una sola experiencia. Desde aquí puedes abrir el catálogo público, compartir productos y mantener el inventario listo para vender.'
                  : `Reúne ${profile.terminology.itemsPluralName.toLowerCase()}, categorías y enlaces de WhatsApp en una sola experiencia. Desde aquí puedes abrir la vitrina pública, compartir enlaces y mantener tu oferta lista.`}
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={openPublicCatalog}
                disabled={!publicCatalogUrl}
                className="inline-flex items-center gap-2 rounded-full bg-[#075f55] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#064e46] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ExternalLink className="h-4 w-4" />
                Abrir {profile.terminology.catalogName.toLowerCase()} público
              </button>
              <button
                type="button"
                onClick={copyCatalogLink}
                disabled={!publicCatalogUrl}
                className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-4 py-2.5 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Copy className="h-4 w-4" />
                Copiar enlace
              </button>
              <button
                type="button"
                onClick={shareCatalogByWhatsApp}
                disabled={!publicCatalogUrl}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Share2 className="h-4 w-4" />
                Compartir por WhatsApp
              </button>
              <Link
                to="/app/products"
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
              >
                <Package className="h-4 w-4" />
                Administrar productos
              </Link>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <StatCard label="Productos" value={loading ? '...' : String(products.length)} icon={Package} />
              <StatCard label="Categorías" value={loading ? '...' : String(categoryCount)} icon={FolderTree} />
              <StatCard label="Destacados" value={loading ? '...' : String(featured.length)} icon={Star} />
            </div>
          </div>

          <div className="rounded-[1.75rem] border border-white/70 bg-white/80 p-4 shadow-lg backdrop-blur">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Ruta pública</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{tenantSlug ? `/${tenantSlug}/catalog` : 'Configura tu tenant'}</p>
              </div>
              <div className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                listo para compartir
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <p className="text-sm text-slate-600">
                El catálogo público permite al vendedor enviar un producto o la vitrina completa por WhatsApp sin salir del panel.
              </p>

              <div className="rounded-2xl bg-slate-950 p-4 text-white">
                <p className="text-xs uppercase tracking-[0.2em] text-white/50">Acciones sugeridas</p>
                <ul className="mt-3 space-y-2 text-sm text-white/85">
                  <li className="flex items-start gap-2"><ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" /> Compartir producto desde la ficha</li>
                  <li className="flex items-start gap-2"><ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" /> Filtrar por categoría para la venta asistida</li>
                  <li className="flex items-start gap-2"><ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" /> Usar el mismo enlace en WhatsApp y en web</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <Link
          to="/app/products"
          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-md"
        >
          <Package className="h-6 w-6 text-[#075f55]" />
          <h2 className="mt-4 text-lg font-bold text-slate-900">Productos</h2>
          <p className="mt-2 text-sm text-slate-600">Crea, edita y comparte cada ítem del catálogo.</p>
        </Link>
        <Link
          to="/app/categories"
          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-md"
        >
          <Tag className="h-6 w-6 text-[#075f55]" />
          <h2 className="mt-4 text-lg font-bold text-slate-900">Categorías</h2>
          <p className="mt-2 text-sm text-slate-600">Ordena el inventario para búsquedas y navegación rápidas.</p>
        </Link>
        <Link
          to="/app/promotions"
          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-md"
        >
          <BadgePercent className="h-6 w-6 text-[#075f55]" />
          <h2 className="mt-4 text-lg font-bold text-slate-900">Promociones</h2>
          <p className="mt-2 text-sm text-slate-600">Prepara descuentos y campañas para la siguiente fase.</p>
        </Link>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Productos recientes</h2>
              <p className="text-sm text-slate-500">Edición rápida para mantener el catálogo al día.</p>
            </div>
            <Link to="/app/products" className="text-sm font-semibold text-[#075f55] hover:underline">
              Ver todos
            </Link>
          </div>

          <div className="mt-4 grid gap-3">
            {loading
              ? Array.from({ length: 3 }).map((_, index) => <div key={index} className="skeleton h-20 rounded-2xl" />)
              : latest.map((product) => (
                  <div key={product.id} className="flex items-center gap-3 rounded-2xl border border-slate-100 p-3">
                    <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl bg-slate-100">
                      {product.imageUrl ? (
                        <img src={product.imageUrl} alt={product.name} className="h-full w-full object-cover" />
                      ) : (
                        <Package className="h-6 w-6 text-slate-300" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-slate-900">{product.name}</p>
                      <p className="text-sm text-slate-500">{product.category || DEFAULT_CATEGORY}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-slate-900">{formatMoney(product.priceCents, product.currency)}</p>
                      <p className="text-xs text-slate-400">Stock {product.stock}</p>
                      <Link
                        to={buildPromotionCreateUrl(product.id)}
                        className="mt-2 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700 transition-colors hover:bg-amber-100"
                      >
                        <Tag className="h-3 w-3" />
                        Promocionar
                      </Link>
                    </div>
                  </div>
                ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-950 p-5 text-white shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/45">Siguiente paso recomendado</p>
          <h2 className="mt-3 text-2xl font-black tracking-tight">Haz que el catálogo llegue al chat</h2>
          <p className="mt-3 text-sm leading-6 text-white/75">
            El panel ya tiene catálogo, productos y categorías. La siguiente capa es conectar esa vitrina pública con el flujo de ventas para que cada producto pueda abrirse y compartirse en segundos.
          </p>

          <div className="mt-5 space-y-3">
            <Link
              to={publicCatalogPath}
              className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            >
              <span>Revisar catálogo público</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/app/products"
              className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            >
              <span>Ir a productos</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

function StatCard({
  label,
  value,
  icon: Icon,
}: {
  label: string
  value: string
  icon: React.ComponentType<{ className?: string }>
}) {
  return (
    <div className="rounded-2xl border border-white/70 bg-white/80 p-4 shadow-sm backdrop-blur">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
          <p className="mt-2 text-2xl font-black text-slate-900">{value}</p>
        </div>
        <div className="rounded-2xl bg-emerald-50 p-3 text-[#075f55]">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  )
}
